/**
 * Home API — Reorder home cards for the active school.
 *
 * PUT /v1/home/admin/order
 *
 * The client sends the ordered public ids of the cards it is currently
 * rendering — a subset of the school's home cards, because the admin is always
 * inside one lifecycle tab. The server permutes those cards among the slots
 * they already occupy in the school's resolved order and renumbers the full
 * set densely, so cards outside the submitted set never move.
 *
 * The position integer itself never crosses the wire: computing positions is
 * the server's job. The response is the school's re-ordered admin list, read
 * through the primary, so the client can seed its cache instead of refetching
 * off a replica that may still be pre-write.
 */

import { z } from 'zod'
import { ListHomeMessagesForAdminResponseSchema } from './list-for-admin'

/**
 * Upper bound on one reorder REQUEST. Well above any real school's home-card
 * set (the largest today is single digits), and low enough that a malformed or
 * hostile payload cannot ask the server to permute an unbounded list.
 *
 * It does NOT bound the write: the server renumbers the school's full home-card
 * set, so the transaction is sized by that set, not by this cap.
 */
export const REORDER_HOME_MESSAGES_MAX = 500

export const ReorderHomeMessagesRequestSchema = z.object({
  /**
   * The rendered cards in their new order. At least two — a one-card list has
   * no order to express.
   */
  publicIds: z.array(z.string().min(1)).min(2).max(REORDER_HOME_MESSAGES_MAX)
})

export type ReorderHomeMessagesRequest = z.infer<typeof ReorderHomeMessagesRequestSchema>

/** The school's full admin list in its new order — same shape as GET /v1/home/admin. */
export const ReorderHomeMessagesResponseSchema = ListHomeMessagesForAdminResponseSchema

export type ReorderHomeMessagesResponse = z.infer<typeof ReorderHomeMessagesResponseSchema>
